import { useEffect, useRef, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { api } from '@/lib/api'
import { Composer } from './Composer'
import { ConversationContextPanel } from './ConversationContextPanel'
import { AiSuggestionBar } from './AiSuggestionBar'

interface Message {
  id: string
  direction: 'INBOUND' | 'OUTBOUND'
  senderType: 'CLIENT' | 'USER' | 'BOT' | 'SYSTEM'
  body: string
  subject: string | null
  sentAt: string | null
  providerStatus: string | null
  aiGenerated: boolean
}

interface ConversationDetail {
  id: string
  channelKind: string
  externalThreadId: string
  status: string
  windowExpiresAt: string | null
  clientStub: {
    id: string
    displayName: string
    email: string | null
    phoneE164: string | null
    dnd: boolean
    dndEmail: boolean
    dndSms: boolean
    dndWhatsapp: boolean
    dndVoice: boolean
    marketingConsentEmail: boolean
    marketingConsentSms: boolean
    marketingConsentWhatsapp: boolean
    timezone: string
  } | null
  messages: Message[]
}

const POLL_MS = 5000

interface ConversationDetailInlineProps {
  conversationId: string
  /** Opcional: si el contenedor ya muestra la lista, oculta la columna de contexto. */
  showContextPanel?: boolean
}

/**
 * Versión inline (sin layout outer) del detalle de conversación. Pensada para
 * embeberse en el split-view del Inbox: la lista vive en la columna izquierda
 * y este componente ocupa el resto del viewport. No incluye link "← volver"
 * porque ya está la lista a la vista.
 */
export function ConversationDetailInline({
  conversationId,
  showContextPanel = true,
}: ConversationDetailInlineProps) {
  const [conv, setConv] = useState<ConversationDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const threadRef = useRef<HTMLDivElement>(null)

  async function load() {
    try {
      const c = await api<ConversationDetail>(
        `/v1/marketing/inbox/conversations/${conversationId}`,
      )
      setConv(c)
      setErr(null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
    const t = setInterval(() => void load(), POLL_MS)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight
    }
  }, [conv?.messages.length])

  async function sendOutbound(body: string, snippetId?: string) {
    try {
      await api(`/v1/marketing/inbox/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body, snippetId, isMarketing: false }),
      })
      await load()
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      throw e
    }
  }

  if (err && !conv) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bp-card p-3 text-sm font-mono text-red-700 border-red-300 bg-red-50">
          {err}
        </div>
      </div>
    )
  }
  if (!conv) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm font-mono text-ink-700/60">
        Cargando…
      </div>
    )
  }

  const windowExpired =
    conv.channelKind === 'WHATSAPP' &&
    conv.windowExpiresAt &&
    new Date(conv.windowExpiresAt) < new Date()

  return (
    <div className="flex h-full flex-1 min-w-0">
      <div className="flex-1 flex flex-col min-w-0">
        <header className="px-4 py-3 border-b border-ink-900/10 bg-white flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="bp-pill">{conv.channelKind}</span>
              <span className="text-sm font-medium truncate">
                {conv.clientStub?.displayName ?? conv.externalThreadId}
              </span>
              {conv.clientStub?.dnd && (
                <span className="bp-pill bg-red-100 text-red-700">DND</span>
              )}
            </div>
            <div className="text-xs font-mono text-ink-700/60 truncate">
              {conv.clientStub?.email ?? conv.clientStub?.phoneE164 ?? conv.externalThreadId}
            </div>
          </div>
          <span className="bp-pill">{conv.status}</span>
        </header>

        {windowExpired && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs font-mono text-amber-900 flex items-center gap-2">
            <AlertTriangle className="size-3.5" />
            Ventana WhatsApp 24h cerrada · sólo se permite enviar mensaje de plantilla
            aprobada por Meta
          </div>
        )}

        <div ref={threadRef} className="flex-1 overflow-auto px-6 py-4 bg-surface">
          <ul className="space-y-2 max-w-2xl mx-auto">
            {conv.messages.length === 0 && (
              <li className="text-center text-sm text-ink-700/60 py-12">(sin mensajes)</li>
            )}
            {conv.messages.map((m) => (
              <li
                key={m.id}
                className={m.direction === 'OUTBOUND' ? 'flex justify-end' : 'flex'}
              >
                <div
                  className={[
                    'max-w-[80%] px-3 py-2 rounded border',
                    m.direction === 'OUTBOUND'
                      ? 'bg-blueprint-600 text-white border-blueprint-700'
                      : 'bg-white border-ink-900/10',
                  ].join(' ')}
                >
                  {m.subject && (
                    <div className="text-xs font-mono uppercase tracking-wider opacity-70 mb-1">
                      {m.subject}
                    </div>
                  )}
                  <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                  <div
                    className={[
                      'text-[10px] font-mono mt-1 flex items-center gap-1',
                      m.direction === 'OUTBOUND' ? 'text-blueprint-100' : 'text-ink-700/50',
                    ].join(' ')}
                  >
                    {m.sentAt ? new Date(m.sentAt).toLocaleString('es-ES') : 'borrador'}
                    {m.providerStatus && <span>· {m.providerStatus}</span>}
                    {m.aiGenerated && <span>· IA</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <AiSuggestionBar conversationId={conv.id} onApproved={() => void load()} />

        <div className="border-t border-ink-900/10 bg-white">
          <Composer onSend={sendOutbound} channel={conv.channelKind} />
          {err && (
            <div data-testid="composer-error" className="px-4 pb-2 text-xs font-mono text-red-700">
              {err}
            </div>
          )}
        </div>
      </div>
      {showContextPanel && (
        <ConversationContextPanel conversationId={conv.id} clientStub={conv.clientStub} />
      )}
    </div>
  )
}
